import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase';
import { useTranslation } from 'react-i18next';
import { 
  collection, 
  onSnapshot, 
  query, 
  orderBy,
  deleteDoc,
  doc,
  updateDoc
} from 'firebase/firestore';
import { 
  Users as UsersIcon, 
  UserPlus, 
  Trash2, 
  ShieldCheck, 
  User, 
  Mail,
  Calendar,
  Loader2,
  Search,
  Settings2,
  X,
  CheckSquare,
  Square,
  Save,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface UserProfile {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'administrator' | 'employee';
  permissions?: Record<string, boolean>;
  createdAt: string;
}

export default function UserManagementPage() {
  const { t } = useTranslation();
  
  const AVAILABLE_PERMISSIONS = [
    { id: 'menu_categories', label: t('admin_nav_categories') },
    { id: 'menu_items', label: t('admin_nav_menu') },
    { id: 'gallery_categories', label: t('admin_nav_gallery_categories') },
    { id: 'gallery_photos', label: t('admin_nav_gallery') },
    { id: 'staff_management', label: t('admin_nav_users') },
  ];

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [tempPermissions, setTempPermissions] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  
  const navigate = useNavigate();

  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const usersData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      })) as UserProfile[];
      setUsers(usersData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleDeleteUser = async (uid: string) => {
    if (window.confirm('Are you sure you want to remove this user? This will NOT delete their Auth account, only their profile.')) {
      await deleteDoc(doc(db, 'users', uid));
    }
  };

  const handleOpenPermissions = (user: UserProfile) => {
    setEditingUser(user);
    setTempPermissions(user.permissions || {
      menu_categories: false,
      menu_items: false,
      gallery_categories: false,
      gallery_photos: false,
      staff_management: false,
    });
    setIsModalOpen(true);
  };

  const togglePermission = (id: string) => {
    setTempPermissions(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSavePermissions = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', editingUser.uid), {
        permissions: tempPermissions
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error updating permissions:", error);
      alert("Failed to update permissions");
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(user => 
    `${user.firstName} ${user.lastName} ${user.email}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ITEMS_PER_PAGE));
  
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [filteredUsers.length, totalPages, currentPage]);

  const currentUsers = filteredUsers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#F57F25]" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('admin_users_title')}</h1>
          <p className="text-white/50 text-sm">{t('admin_users_sub')}</p>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20" size={20} />
        <input 
          type="text"
          placeholder={t('admin_users_search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-[#111] border border-white/5 rounded-2xl py-4 pl-12 pr-6 text-white focus:border-[#F57F25] outline-none transition-all"
        />
      </div>

      {/* Users Table */}
      <div className="bg-[#111] border border-white/5 rounded-[32px] overflow-hidden shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-black/50 border-b border-white/5">
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_users_member')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_users_email')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_users_role')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">{t('admin_users_joined')}</th>
                <th className="px-8 py-5 text-[10px] font-black text-white/30 uppercase tracking-[0.2em] text-right">{t('admin_users_actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {currentUsers.map((user) => (
                <tr key={user.uid} className="hover:bg-white/[0.02] transition-colors group">
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#F57F25]/10 rounded-xl flex items-center justify-center text-[#F57F25]">
                        <User size={20} />
                      </div>
                      <div>
                        <p className="text-white font-bold text-sm">{user.firstName} {user.lastName}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <div className="flex items-center gap-2 text-white/50 text-sm">
                      <Mail size={14} />
                      {user.email}
                    </div>
                  </td>
                  <td className="px-8 py-5">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                      user.role === 'administrator' 
                        ? 'bg-[#F57F25]/10 text-[#F57F25] border border-[#F57F25]/20' 
                        : 'bg-white/5 text-white/50 border border-white/10'
                    }`}>
                      {user.role === 'administrator' && <ShieldCheck size={12} />}
                      {user.role}
                    </span>
                  </td>
                  <td className="px-8 py-5 text-white/30 text-xs font-medium">
                    <div className="flex items-center gap-2">
                      <Calendar size={14} />
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-8 py-5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button 
                        onClick={() => handleOpenPermissions(user)}
                        className="p-2 text-white/10 hover:text-[#F57F25] hover:bg-[#F57F25]/10 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                        title={t('admin_users_manage_perm')}
                      >
                        <Settings2 size={18} />
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user.uid)}
                        className="p-2 text-white/10 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all opacity-0 group-hover:opacity-100"
                        title={t('admin_users_delete')}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-white/5 bg-black/20">
            <p className="text-white/40 text-xs font-bold uppercase tracking-widest">
              {t('admin_pagination_page')} {currentPage} {t('admin_pagination_of')} {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 transition-colors"
                title={t('admin_pagination_prev')}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center justify-center w-8 h-8 rounded-lg bg-white/5 text-white hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 transition-colors"
                title={t('admin_pagination_next')}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Permissions Modal */}
      {isModalOpen && editingUser && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] w-full max-w-lg rounded-[40px] border border-white/10 shadow-2xl overflow-hidden">
            <div className="p-8 border-b border-white/5 flex justify-between items-center bg-[#181818]">
              <div>
                <h2 className="text-2xl font-black text-white uppercase tracking-tight">{t('admin_users_permissions')}</h2>
                <p className="text-[#F57F25] text-[10px] font-black uppercase tracking-widest mt-1">
                  {editingUser.firstName} {editingUser.lastName}
                </p>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="text-white/40 hover:text-white transition-colors">
                <X size={28} />
              </button>
            </div>

            <div className="p-8 md:p-10 space-y-6">
              <div className="grid grid-cols-1 gap-3">
                {AVAILABLE_PERMISSIONS.map(perm => (
                  <button
                    key={perm.id}
                    onClick={() => togglePermission(perm.id)}
                    className={`flex items-center justify-between p-5 rounded-2xl border transition-all ${
                      tempPermissions[perm.id]
                        ? 'bg-[#F57F25]/10 border-[#F57F25] text-[#F57F25]'
                        : 'bg-black border-white/5 text-white/40 hover:border-white/20'
                    }`}
                  >
                    <span className="font-bold text-sm uppercase tracking-tight">{perm.label}</span>
                    {tempPermissions[perm.id] ? <CheckSquare size={20} /> : <Square size={20} />}
                  </button>
                ))}
              </div>

              <div className="pt-4">
                <button
                  onClick={handleSavePermissions}
                  disabled={saving}
                  className="w-full bg-[#F57F25] hover:bg-[#e06d15] text-black font-black py-5 rounded-[24px] transition-all shadow-xl shadow-[#F57F25]/20 flex items-center justify-center gap-3 disabled:opacity-50 uppercase text-sm"
                >
                  {saving ? <Loader2 className="animate-spin" size={20} /> : <><Save size={20} /> {t('admin_users_update_access')}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredUsers.length === 0 && (
        <div className="text-center py-20 bg-[#111] rounded-[32px] border border-dashed border-white/10">
          <p className="text-white/20 font-bold uppercase tracking-widest text-sm">{t('admin_users_empty')}</p>
        </div>
      )}
    </div>
  );
}
